import { Avatar, Link, Paper, Stack, Typography } from '@mui/material';
import { AndieChatProvider } from '@/providers/andie-chat-provider';
import { styled, useTheme } from '@mui/material/styles';
import { Chat } from '@/components/onboarding/Chat';
import { Trans, useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { lightBlue, orange, yellow } from '@mui/material/colors';
import { CreditCardRounded, StarHalfRounded, StarRounded } from '@mui/icons-material';
import posthog from 'posthog-js';
import { COOKIES } from '@/constants';
import Cookies from 'js-cookie';

const ResponsiveStack = styled(Stack)(({ theme }) => ({
    [theme.breakpoints.down('md')]: {
        flexDirection: 'column-reverse',
    },
}));

export default function Onboarding() {
    const { i18n, t } = useTranslation();
    const location = useLocation();
    const theme = useTheme();
    const [onboardingStarted, setOnboardingStarted] = useState(false);

    // Set language based on parameter or browser setting
    useEffect(() => {
        const language = new URLSearchParams(location.search).get('language') || navigator.language;
        // i18n.language keeps whatever case it is given, and our locale maps are keyed lowercase ('pt-br')
        i18n.changeLanguage(language.toLowerCase());
    }, [i18n, location.search]);

    const reviews = [t('onboarding.reviews.0'), t('onboarding.reviews.1'), t('onboarding.reviews.2')];

    const handleChatStart = () => {
        posthog.capture('onboarding.homepage.started');
        window.parent.postMessage({ type: 'onboardingStarted' }, import.meta.env.VITE_HOMEPAGE_ROOT);
        setOnboardingStarted(true);
    };

    const handleChatEnd = (option: string) => {
        const onboardingId = option === 'import' ? Cookies.get(COOKIES.ONBOARDING_ID) : undefined;

        if (option !== 'import') {
            Cookies.remove(COOKIES.ONBOARDING_ID);
        }

        if (window.top) {
            const url = new URL(`${import.meta.env.VITE_LEGACY_APP_ROOT}/posts/calendar`);
            if (onboardingId) {
                url.searchParams.set('onboardingId', onboardingId);
            }
            window.top.location.href = url.toString();
        }
    };

    return (
        <Stack height="100vh" width={'100vw'} overflow="auto" bgcolor={'primary.main'}>
            <AndieChatProvider publicMode initialHydrated>
                <ResponsiveStack direction="column" alignItems="center" justifyContent="center" flex={1}>
                    <Stack sx={{ maxWidth: 1000, width: '100%', my: 4 }}>
                        {!onboardingStarted ? (
                            <>
                                <Stack
                                    direction={'row'}
                                    justifyContent="center"
                                    alignItems="center"
                                    gap={5}
                                    flexWrap="wrap"
                                >
                                    {reviews.map((text, idx) => (
                                        <Stack
                                            key={idx}
                                            direction="column"
                                            justifyContent="center"
                                            alignItems="center"
                                            spacing={1}
                                            sx={
                                                idx <= 1
                                                    ? {
                                                          display: {
                                                              xs: 'none',
                                                              sm: 'inherit',
                                                          },
                                                      }
                                                    : {}
                                            }
                                        >
                                            <Stack direction="row">
                                                {Array.from({ length: 5 }).map((_, starIdx) => (
                                                    <StarRounded key={starIdx} sx={{ color: yellow[600] }} />
                                                ))}
                                            </Stack>
                                            <Typography color="primary.contrastText" textAlign="center">
                                                "{text}"
                                            </Typography>
                                        </Stack>
                                    ))}
                                </Stack>
                                <Stack color="primary.contrastText" textAlign="center" mb={4} mt={4} px={2}>
                                    <Typography
                                        variant="h2"
                                        fontWeight={700}
                                        lineHeight={1}
                                        fontSize={{ xs: 32, sm: 40, md: 56 }}
                                    >
                                        <Trans
                                            i18nKey="onboarding.onboarding.title"
                                            components={{
                                                d: <span style={{ color: lightBlue[50], opacity: 0.7 }} />,
                                                br: <br />,
                                            }}
                                        />
                                    </Typography>
                                    <Stack
                                        direction="column"
                                        alignItems="center"
                                        justifyContent="center"
                                        spacing={0}
                                        mt={3}
                                    >
                                        <Typography variant="h4">
                                            {t('onboarding.onboarding.subtitle')}
                                        </Typography>
                                    </Stack>
                                </Stack>
                            </>
                        ) : (
                            <Stack m={4} alignItems="center" justifyContent="center">
                                <img
                                    src="/swonkie-lettering.svg"
                                    alt="Swonkie logo"
                                    height={40}
                                    style={{
                                        filter: 'brightness(0) invert(1)',
                                    }}
                                />
                            </Stack>
                        )}
                        <Paper
                            elevation={5}
                            sx={{
                                borderRadius: 4,
                                m: 1,
                                position: 'relative',
                                '&:after': {
                                    content: '""',
                                    position: 'absolute',
                                    top: '-3px',
                                    left: '-3px',
                                    height: 'calc(100% + 6px)',
                                    width: 'calc(100% + 6px)',
                                    background: (theme) =>
                                        `linear-gradient(60deg, ${theme.palette.primary.light}, ${theme.palette.secondary.light}, ${theme.palette.error.light}, ${theme.palette.warning.light}, ${theme.palette.info.light}, ${theme.palette.success.light})`,
                                    borderRadius: 4,
                                    animation: 'animated-gradient 3s ease alternate infinite',
                                    backgroundSize: '300% 300%',
                                },
                                '@keyframes animated-gradient': {
                                    '0%': { backgroundPosition: '0% 50%' },
                                    '50%': { backgroundPosition: '100% 50%' },
                                    '100%': { backgroundPosition: '0% 50%' },
                                },
                                '&:before': {
                                    content: '""',
                                    position: 'absolute',
                                    top: '0px',
                                    left: '0px',
                                    height: '100%',
                                    width: '100%',
                                    bgcolor: (theme) => theme.palette.background.paper,
                                    borderRadius: 4,
                                    zIndex: 1,
                                },
                                ...(onboardingStarted && { mb: { xs: '30vh', sm: 1 } }),
                            }}
                        >
                            <Chat onStart={handleChatStart} onEnd={handleChatEnd} />
                        </Paper>
                        {!onboardingStarted && (
                            <Stack
                                direction={{ xs: 'column', sm: 'row' }}
                                alignItems="center"
                                justifyContent="center"
                                spacing={5}
                                color={theme.palette.primary.contrastText}
                                m={3}
                            >
                                <Stack direction="row" alignItems="center" spacing={1}>
                                    <CreditCardRounded />
                                    <Typography>{t('onboarding.no-credit-card-required')}</Typography>
                                </Stack>
                                <Link
                                    href="https://www.g2.com/products/swonkie/reviews"
                                    target="_blank"
                                    underline="none"
                                    color="inherit"
                                >
                                    <Stack direction="row" alignItems="center" spacing={0.5}>
                                        <Avatar
                                            src="https://company.g2.com/hs-fs/hubfs/brand-guide/g2-logo-white-rorange.png?width=95&name=g2-logo-white-rorange.png"
                                            sx={{ width: 32, height: 32 }}
                                        />
                                        <Stack direction="column" alignItems="center" spacing={0}>
                                            <Stack direction="row">
                                                {Array.from({ length: 4 }).map((_, starIdx) => (
                                                    <StarRounded key={starIdx} sx={{ color: orange[900] }} />
                                                ))}
                                                <StarHalfRounded sx={{ color: orange[900] }} />
                                            </Stack>
                                            <Typography variant="caption">
                                                {t('onboarding.g2-rating')}
                                            </Typography>
                                        </Stack>
                                    </Stack>
                                </Link>
                            </Stack>
                        )}
                    </Stack>
                </ResponsiveStack>
            </AndieChatProvider>
        </Stack>
    );
}
